import React, { useEffect, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, Check, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';
import { TRADE_DATA } from '../data/tradeData';
import { openBookingModal } from '../components/BookingModal';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const TradeLanding = () => {
  const { tradeSlug } = useParams();
  const trade = TRADE_DATA[tradeSlug];
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observerRef.current.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08 });

    document.querySelectorAll('.reveal:not(.visible)').forEach(el => {
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [tradeSlug]);

  if (!trade) return <Navigate to="/" replace />;

  const TradeIcon = trade.icon;

  const seoData = {
    title: `${trade.name} Lead Generation | Exclusive Leads & Booked Estimates`,
    description: `Get exclusive ${trade.name.toLowerCase()} leads and booked estimates with Skye AI. Done-for-you AI growth system. ${trade.stats.hasRealData ? `Real results: ${trade.stats.roas ? trade.stats.roas + ' ROAS' : ''}${trade.stats.revenue ? ' ' + trade.stats.revenue + ' revenue' : ''}` : '$987K+ generated across home service campaigns.'}`,
    keywords: `${trade.name.toLowerCase()} leads, ${trade.name.toLowerCase()} marketing, ${trade.name.toLowerCase()} lead generation, exclusive ${trade.name.toLowerCase()} leads, booked estimates ${trade.name.toLowerCase()}`,
    path: `/${trade.slug}`,
  };

  return (
    <div className="trade-landing" data-testid={`trade-landing-${trade.slug}`}>
      <SEO {...seoData} />

      {/* Hero */}
      <section className="tl-hero" style={{ '--trade-color': trade.color }}>
        <div className="tl-hero__bg" />
        <div className="container">
          <div className="tl-hero__content reveal">
            <div className="tl-hero__badge" data-testid="trade-badge">
              <TradeIcon size={16} />
              <span>AI Growth System for {trade.name}</span>
            </div>
            <h1 className="tl-hero__title" data-testid="trade-hero-title">{trade.tagline}</h1>
            <p className="tl-hero__subtitle">{trade.subtitle}</p>
            <div className="tl-hero__guarantee">
              <Sparkles size={16} />
              <span>50+ exclusive leads & 15%+ more booked estimates in 90 days — or you don't pay.</span>
            </div>
            <button
              onClick={openBookingModal}
              className="tl-hero__cta btn-gradient"
              data-testid="trade-hero-cta"
            >
              Book My AI Growth Planning Call <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      {trade.stats.hasRealData && (
        <section className="tl-stats reveal">
          <div className="container">
            <div className="tl-stats__grid" data-testid="trade-stats">
              {trade.stats.roas && (
                <div className="tl-stats__item">
                  <span className="tl-stats__value" style={{ color: trade.color }}>{trade.stats.roas}</span>
                  <span className="tl-stats__label">Campaign ROAS</span>
                </div>
              )}
              {trade.stats.revenue && (
                <div className="tl-stats__item">
                  <span className="tl-stats__value" style={{ color: trade.color }}>{trade.stats.revenue}</span>
                  <span className="tl-stats__label">Revenue Generated</span>
                </div>
              )}
              {trade.stats.clients && (
                <div className="tl-stats__item">
                  <span className="tl-stats__value" style={{ color: trade.color }}>{trade.stats.clients}</span>
                  <span className="tl-stats__label">New Clients</span>
                </div>
              )}
            </div>
            <p className="tl-stats__note">{trade.statsNote}</p>
          </div>
        </section>
      )}

      {/* Pain Points */}
      <section className="tl-pain">
        <div className="container">
          <div className="tl-pain__header reveal">
            <h2>Sound Familiar?</h2>
            <p>Most {trade.name.toLowerCase()} businesses deal with the same problems.</p>
          </div>
          <div className="tl-pain__grid reveal">
            {trade.painPoints.map((point, i) => (
              <div key={`pain-${i}`} className="tl-pain__card">
                <div className="tl-pain__number">{i + 1}</div>
                <p>{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Fix It */}
      <section className="tl-features" style={{ '--trade-color': trade.color }}>
        <div className="container">
          <div className="tl-features__header reveal">
            <h2>How Skye AI Fixes This for {trade.name}</h2>
            <p>Your dedicated 5-person team builds and runs everything.</p>
          </div>
          <div className="tl-features__grid">
            {trade.features.map((feature, i) => (
              <div key={`feat-${i}`} className="tl-features__card reveal" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="tl-features__card-accent" style={{ background: trade.color }} />
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Types */}
      <section className="tl-jobs reveal">
        <div className="container">
          <h2>{trade.name} Services We Generate Leads For</h2>
          <div className="tl-jobs__grid">
            {trade.jobTypes.map((job) => (
              <div key={job} className="tl-jobs__item">
                <Check size={16} style={{ color: trade.color }} />
                <span>{job}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="tl-faq">
        <div className="container">
          <div className="tl-faq__header reveal">
            <h2>{trade.name} FAQ</h2>
          </div>
          <div className="tl-faq__content reveal">
            <Accordion type="single" collapsible className="faq-accordion">
              {trade.faqs.map((faq, i) => (
                <AccordionItem key={`faq-${i}`} value={`faq-${i}`}>
                  <AccordionTrigger className="faq-question">{faq.q}</AccordionTrigger>
                  <AccordionContent className="faq-answer">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="tl-cta" style={{ '--trade-color': trade.color }}>
        <div className="container">
          <div className="tl-cta__inner reveal">
            <h2>Ready to Fill Your {trade.name} Schedule?</h2>
            <p>50+ exclusive leads & 15%+ more booked estimates in 90 days — or you don't pay.</p>
            <button
              onClick={openBookingModal}
              className="btn-gradient btn-lg"
              data-testid="trade-cta-bottom"
            >
              Book My AI Growth Planning Call <ArrowRight size={18} />
            </button>
            <Link to="/case-studies" className="tl-cta__link">
              See our campaign results <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TradeLanding;
